import { useEffect, useState } from 'react';
import { useChatsStore } from '../stores/chatsStore';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 4H14M5.333 4V2.667C5.333 2.298 5.632 2 6 2H10C10.368 2 10.667 2.298 10.667 2.667V4M12.667 4V13.333C12.667 13.702 12.368 14 12 14H4C3.632 14 3.333 13.702 3.333 13.333V4" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M3 4.5C3 3.397 3.897 2.5 5 2.5H15C16.103 2.5 17 3.397 17 4.5V11.5C17 12.603 16.103 13.5 15 13.5H7.5L3 17.5V13.5H5C3.897 13.5 3 12.603 3 11.5V4.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M19 19L14.667 14.667M17 9.5C17 13.6421 13.6421 17 9.5 17C5.35786 17 2 13.6421 2 9.5C2 5.35786 5.35786 2 9.5 2C13.6421 2 17 5.35786 17 9.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 7C9.657 7 11 5.657 11 4C11 2.343 9.657 1 8 1C6.343 1 5 2.343 5 4C5 5.657 6.343 7 8 7Z" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 14C14 11.791 12.209 10 10 10H6C3.791 10 2 11.791 2 14" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="bg-bg-secondary border border-border-color rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-text-primary mb-2">{title}</h3>
        <p className="text-text-secondary mb-6">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 px-4 bg-bg-tertiary hover:bg-bg-primary text-text-primary rounded-lg font-medium transition-colors">
            Отмена
          </button>
          <button onClick={onConfirm} className="flex-1 py-2.5 px-4 bg-error hover:bg-red-600 text-white rounded-lg font-medium transition-colors">
            Удалить
          </button>
        </div>
      </div>
    </div>
  );
};

// Список чатов
export const ChatsList = () => {
  const { chats, total, isLoading, userId, fetchChats, deleteChat, clearUserIdFilter } = useChatsStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, id: null });
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const limit = 20;

  // Инициализация: проверяем URL параметр user_id
  useEffect(() => {
    const userIdParam = searchParams.get('user_id');
    if (userIdParam) {
      setSearchInput(userIdParam);
      fetchChats({ limit, offset: 0, user_id: userIdParam });
    } else {
      fetchChats({ limit, offset: page * limit });
    }
  }, [searchParams.get('user_id')]);

  // Поиск по vk_user_id
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    if (searchInput.trim()) {
      setSearchParams({ user_id: searchInput.trim() });
      fetchChats({ limit, offset: 0, user_id: searchInput.trim() });
    } else {
      setSearchParams({});
      clearUserIdFilter();
      fetchChats({ limit, offset: 0 });
    }
  };

  // Сбросить поиск
  const handleClearSearch = () => {
    setSearchInput('');
    setPage(0);
    setSearchParams({});
    clearUserIdFilter();
    fetchChats({ limit, offset: 0 });
  };

  const handleDelete = (id) => {
    setConfirmModal({ isOpen: true, id });
  };

  const handleConfirmDelete = async () => {
    if (confirmModal.id) {
      await deleteChat(confirmModal.id);
      setConfirmModal({ isOpen: false, id: null });
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-text-primary">Чаты</h1>
        <div className="text-sm text-text-secondary">
          Всего: <span className="text-text-primary font-medium">{total}</span>
        </div>
      </div>

      {/* Поиск по пользователю */}
      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Поиск по VK ID пользователя..."
            className="w-full pl-10 pr-10 py-2.5 bg-bg-secondary border border-border-color rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
            <SearchIcon />
          </div>
          {searchInput && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
            >
              <CloseIcon />
            </button>
          )}
        </div>
        <button
          type="submit"
          className="px-6 py-2.5 bg-accent-primary hover:bg-accent-hover text-white rounded-lg font-medium transition-colors"
        >
          Найти
        </button>
      </form>

      {/* Показываем активный фильтр */}
      {userId && (
        <div className="flex items-center gap-2 px-4 py-3 bg-accent-primary/10 border border-accent-primary/20 rounded-lg">
          <UserIcon />
          <span className="text-sm text-text-primary">
            Чаты пользователя: <span className="font-mono font-medium">{userId}</span>
          </span>
          <button
            onClick={handleClearSearch}
            className="ml-auto text-text-secondary hover:text-text-primary"
          >
            <CloseIcon />
          </button>
        </div>
      )}

      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-accent-primary border-t-transparent rounded-full" />
          </div>
        ) : !chats || chats.length === 0 ? (
          <div className="bg-bg-secondary rounded-xl border border-border-color p-12 text-center text-text-secondary">
            {userId ? 'У этого пользователя нет чатов' : 'Чаты не найдены'}
          </div>
        ) : (
          chats.map((chat) => (
            <div
              key={chat.id}
              className="bg-bg-secondary rounded-xl border border-border-color p-4 hover:bg-bg-tertiary/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div
                  className="flex-1 cursor-pointer"
                  onClick={() => navigate(`/chats/${chat.id}`)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-text-secondary">
                      <ChatIcon />
                    </div>
                    <h3 className="font-semibold text-text-primary">{chat.title}</h3>
                    {chat.user_disabled && (
                      <span className="px-2 py-0.5 text-xs bg-error/10 text-error rounded">
                        Пользователь заблокирован
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-text-secondary space-y-1">
                    <div>ID чата: <span className="font-mono">{chat.id}</span></div>
                    <div>
                      VK ID:{' '}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/users?search=${chat.vk_user_id}`);
                        }}
                        className="font-mono text-accent-primary hover:underline"
                      >
                        {chat.vk_user_id}
                      </button>
                    </div>
                    <div>Сообщений: {chat.message_count}</div>
                    <div>Обновлён: {new Date(chat.updated_at).toLocaleDateString('ru-RU')}</div>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(chat.id)}
                  className="p-2 text-text-secondary hover:text-error transition-colors"
                  title="Удалить чат"
                >
                  <TrashIcon />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-text-secondary">
            Страница {page + 1} из {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-4 py-2 bg-bg-secondary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
            >
              Назад
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-4 py-2 bg-bg-secondary border border-border-color rounded-lg text-text-primary disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bg-tertiary transition-colors"
            >
              Вперёд
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Удалить чат?"
        message="Все сообщения в этом чате будут удалены."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmModal({ isOpen: false, id: null })}
      />
    </div>
  );
};

// Просмотр чата
export const ChatDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentChat, fetchChat, isLoading, clearCurrentChat } = useChatsStore();

  useEffect(() => {
    fetchChat(id);
    return () => clearCurrentChat();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-accent-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!currentChat) {
    return (
      <div className="text-center text-text-secondary py-12">
        Чат не найден
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/chats')}
          className="p-2 text-text-secondary hover:text-text-primary transition-colors"
        >
          <BackIcon />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-text-primary">{currentChat.title}</h1>
          <div className="text-sm text-text-secondary">
            Пользователь: {currentChat.vk_user_id}
          </div>
        </div>
      </div>

      <div className="bg-bg-secondary rounded-xl border border-border-color overflow-hidden">
        <div className="max-h-[600px] overflow-y-auto p-4 space-y-4">
          {currentChat.messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-accent-primary text-white'
                    : 'bg-bg-tertiary text-text-primary'
                }`}
              >
                <div className="text-sm font-medium mb-1 opacity-75">
                  {msg.role === 'user' ? 'Вы' : msg.model || 'Ассистент'}
                </div>
                <div className="whitespace-pre-wrap">{msg.content}</div>
                <div className="text-xs mt-2 opacity-50">
                  {new Date(msg.created_at).toLocaleString('ru-RU')}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Главный компонент Chats
export const Chats = () => {
  const { id } = useParams();
  
  if (id) {
    return <ChatDetail />;
  }
  
  return <ChatsList />;
};
